import { Store } from 'vuex'
import RootState from '@vue-storefront/core/types/RootState'
import Vue from 'vue'
import { isServer } from '@vue-storefront/core/helpers/isServer'
import { once } from '@vue-storefront/core/helpers/once'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'
import i18n from '@vue-storefront/i18n'
import VueRouter from 'vue-router'
import VueLazyload from 'vue-lazyload'
import Vuelidate from 'vuelidate'
import Meta from 'vue-meta'
import { sync } from 'vuex-router-sync'
import VueObserveVisibility from 'vue-observe-visibility'
// TODO simplify by removing global mixins, plugins and filters - it can be done in normal 'vue' way
import { registerTheme } from '@vue-storefront/core/lib/themes'
import { themeEntry } from 'theme/index.js'
import { registerModules } from '@vue-storefront/core/lib/module'
import { prepareStoreView, setupMultistoreRoutes } from '@vue-storefront/core/lib/multistore'
import * as coreMixins from '@vue-storefront/core/mixins'
import * as coreFilters from '@vue-storefront/core/filters'
import * as corePlugins from '@vue-storefront/core/compatibility/plugins'
import store from '@vue-storefront/core/store'
import { enabledModules } from './modules-entry'
import globalConfig from 'config'
import { injectReferences } from '@vue-storefront/core/lib/modules'
import { coreHooksExecutors } from '@vue-storefront/core/hooks'
import { registerClientModules } from 'src/modules/client'
import initialStateFactory from '@vue-storefront/core/helpers/initialStateFactory'
import { createRouter, createRouterProxy } from '@vue-storefront/core/helpers/router'
import { checkForIntlPolyfill } from '@vue-storefront/i18n/intl'
import themeRoutes from 'theme/router'
import { RouterManager } from '@vue-storefront/core/lib/router-manager'
import { beforeEachGuard } from '@vue-storefront/core/modules/url/router/beforeEach'
import { captureSsrStoreBaseline } from './scripts/utils/ssr-store-reset'

const stateFactory = initialStateFactory(store.state)

let router: VueRouter = null
let routerProxy: VueRouter = null
/** Returned by vuex-router-sync `sync`; must run before a second `sync` on the singleton store. */
let unsyncRouterFromStore: (() => void) | null = null

function ensureUrlGuardRegistered (routerInstance: VueRouter): void {
  const marker = '__VSF_URL_GUARD_REGISTERED__'
  if (!(routerInstance as any)[marker]) {
    routerInstance.beforeEach(beforeEachGuard)
    ;(routerInstance as any)[marker] = true
  }
}

once('__VUE_EXTEND_RR__', () => {
  Vue.use(VueRouter)
})

const createApp = async (ssrContext, config, storeCode = null): Promise<{app: Vue, router: VueRouter, store: Store<RootState>, initialState: RootState}> => {
  router = createRouter()
  routerProxy = createRouterProxy(router)
  RouterManager.reset()
  // sync router with vuex `route` module — unregister any previous sync on this singleton store
  // (e.g. second SSR request or dev re-entry) or Vuex warns: state field "route" was overridden…
  if (unsyncRouterFromStore) {
    unsyncRouterFromStore()
    unsyncRouterFromStore = null
  }
  unsyncRouterFromStore = sync(store, routerProxy) as () => void
  // TODO: Don't mutate the state directly, use mutation instead
  store.state.version =
    (typeof process !== 'undefined' && process.env && process.env.APPVERSION) ||
    (typeof globalThis !== 'undefined' && (globalThis as any).APPVERSION) ||
    ''
  store.state.config = config // @deprecated
  store.state.__DEMO_MODE__ = (config.demomode === true)
  if (ssrContext) {
    // @deprecated - we shouldn't share server context between requests
    Vue.prototype.$ssrRequestContext = {
      output: {
        cacheTags: ssrContext.output.cacheTags
      },
      userAgent: ssrContext.server.request.headers['user-agent']
    }

    Vue.prototype.$cacheTags = ssrContext.output.cacheTags
  }
  if (!store.state.config) store.state.config = globalConfig //  @deprecated - we should avoid the `config`
  const storeView = await prepareStoreView(storeCode) // prepare the default storeView
  store.state.storeView = storeView

  // @deprecated from 2.0
  once('__VUE_EXTEND__', () => {
    Vue.use(Vuelidate)
    Vue.use(VueLazyload, { attempt: 2, preLoad: 1.5 })
    Vue.use(Meta, {
      ssrAppId: 1
    })
    Vue.use(VueObserveVisibility)

    Object.keys(corePlugins).forEach(key => {
      Vue.use(corePlugins[key])
    })

    Object.keys(coreMixins).forEach(key => {
      Vue.mixin(coreMixins[key])
    })

    Object.keys(coreFilters).forEach(key => {
      Vue.filter(key, coreFilters[key])
    })
  })

  const appContext = {
    isServer,
    ssrContext
  }

  // Router installs before theme routes if we `new Vue` first: the initial transitionTo('/')
  // runs with an empty matcher, the URL dispatcher maps `/` to page-not-found, and the client
  // then keeps that mapping. Register and flush static theme routes before the root instance exists.
  const appForModuleRefs = Object.create(Vue.prototype) as Vue
  injectReferences(appForModuleRefs, store, routerProxy, config)
  registerClientModules()
  // `registerModule()` is globally deduped, so UrlModule may not run on subsequent createApp calls.
  // Ensure the URL dispatcher guard exists on every fresh router instance (SSR/client).
  ensureUrlGuardRegistered(routerProxy)
  registerModules(enabledModules, appContext)
  setupMultistoreRoutes(config, routerProxy, themeRoutes as any, 10)
  RouterManager.flushRouteQueue()

  const vueOptions = {
    router: routerProxy,
    store,
    i18n,
    render: h => h(themeEntry)
  }

  const app = new Vue(vueOptions)

  injectReferences(app, store, routerProxy, globalConfig)
  registerTheme(globalConfig.theme, app, routerProxy, store, globalConfig, ssrContext)

  await checkForIntlPolyfill(storeView)

  coreHooksExecutors.afterAppInit()
  // @deprecated from 2.0
  EventBus.$emit('application-after-init', app)

  if (ssrContext) {
    captureSsrStoreBaseline()
  }

  return { app, router: routerProxy, store, initialState: stateFactory.createInitialState(store.state) }
}

export { routerProxy as router, createApp, router as baseRouter }
